// landing/src/screens/RegisterScreen.jsx
import { useState, useEffect, useRef } from 'react';

const C = {
  bg:      '#1c1c1c',
  surface: '#2a2a2a',
  card:    '#2a2a2a',
  input:   '#1c1c1c',
  border:  '#444',
  orange:  '#ff7a00',
  muted:   '#888',
  text:    '#fff',
  danger:  '#c0392b',
  success: '#2a6b2a',
};

const VENUE_TYPES = [
  { value: 'bar',         label: 'Bar' },
  { value: 'restaurant',  label: 'Restaurant' },
  { value: 'hotel',       label: 'Hotel' },
  { value: 'cafe',        label: 'Café' },
  { value: 'club',        label: 'Club / Lounge' },
  { value: 'canteen',     label: 'Canteen' },
  { value: 'fast_food',   label: 'Fast Food' },
  { value: 'other',       label: 'Other' },
];

const PLANS = [
  {
    value:    'mzigo',
    label:    'Mzigo',
    price:    'KES 999/mo',
    features: ['Up to 3 staff', 'Up to 10 tables', 'M-Pesa payments', 'WhatsApp alerts', 'Daily report'],
  },
  {
    value:    'biz',
    label:    'Biz',
    price:    'KES 1,999/mo',
    features: ['Up to 10 staff', 'Up to 30 tables', 'Everything in Mzigo', 'Happy hour pricing', 'Bulk price updates', 'Inventory management'],
    popular:  true,
  },
  {
    value:    'club',
    label:    'Club',
    price:    'KES 3,999/mo',
    features: ['Unlimited staff', 'Unlimited tables', 'Everything in Biz', 'AI sales insights', 'AI inventory commands', 'Margin tracking'],
  },
];

// ── PIN pad ────────────────────────────────────────────────────────────────────

function PinPad({ label, value, onChange }) {
  const handleKey = (key) => {
    if (key === 'DEL') return onChange(value.slice(0, -1));
    if (value.length < 4) onChange(value + key);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <label style={s.label}>{label}</label>
      <div style={s.pinDots}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ ...s.dot, background: value.length > i ? C.orange : C.input }} />
        ))}
      </div>
      <div style={s.numpad}>
        {['1','2','3','4','5','6','7','8','9','','0','DEL'].map((key, i) => (
          <button
            key={i}
            type="button"
            style={{ ...s.numKey, opacity: key === '' ? 0 : 1 }}
            onClick={() => key && handleKey(key)}
            disabled={!key}
          >
            {key}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main form ──────────────────────────────────────────────────────────────────

export default function RegisterScreen({ onSuccess }) {
  const [form, setForm] = useState({
    name:        '',
    short_name:  '',
    type:        '',
    owner_name:  '',
    owner_phone: '',
    mpesa_phone: '',
    email:       '',
    county:      '',
    town:        '',
    plan:        'biz', // default to most popular
    pin:         '',
    confirm_pin: '',
    ref_code:    '',
  });

  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  // Read ref code from URL silently — ?ref=AGT-00001
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setForm(f => ({ ...f, ref_code: ref }));
  }, []);

  const set = (field) => (e) =>
    setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    setError('');

    // Client-side validation
    if (!form.name.trim())        return setError('Venue name is required');
    if (!form.type)               return setError('Select a venue type');
    if (!form.owner_name.trim())  return setError('Owner name is required');
    if (form.owner_phone.length < 10) return setError('Enter a valid phone number');
    if (!form.plan)               return setError('Select a plan');
    if (form.pin.length !== 4)    return setError('PIN must be 4 digits');
    if (form.confirm_pin.length !== 4) return setError('Please confirm your PIN');
    if (form.pin !== form.confirm_pin) return setError('PINs do not match');
    if (form.short_name && form.short_name.length > 20)
      return setError('Short name must be 20 characters or less');

    setLoading(true);
    try {
      const API = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const res = await fetch(`${API}/auth/register-venue`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok) {
        return setError(data.error || 'Registration failed. Please try again.');
      }

      onSuccess(data);
    } catch (_) {
      setError('Network error. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const shortNamePlaceholder = form.name
    ? form.name.replace(/[^\w\s]/g, '').trim().slice(0, 20)
    : 'Auto-generated';

  return (
    <div style={s.page}>
      <div style={s.container}>

        {/* Header */}
        <div style={s.header}>
          <h1 style={s.logo}>ServeIQ</h1>
          <p style={s.subtitle}>Set up your venue — takes 2 minutes</p>
          {form.ref_code && (
            <div style={s.refBadge}>
              🤝 Referred by an agent · Code: {form.ref_code}
            </div>
          )}
        </div>

        {/* Section: Venue Details */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Venue Details</h2>

          <label style={s.label}>Venue Name *</label>
          <input style={s.input} placeholder="e.g. Kava Café Westlands"
            value={form.name} onChange={set('name')} />

          <label style={s.label}>
            Short Name
            <span style={s.hint}> — shown on login screen (max 20 chars)</span>
          </label>
          <div style={{ position: 'relative' }}>
            <input style={s.input} placeholder={shortNamePlaceholder}
              value={form.short_name} onChange={set('short_name')} maxLength={20} />
            <span style={s.charCount}>{form.short_name.length}/20</span>
          </div>

          <label style={s.label}>Venue Type *</label>
          <select style={s.select} value={form.type} onChange={set('type')}>
            <option value="">Select type…</option>
            {VENUE_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          <div style={s.row}>
            <div style={s.half}>
              <label style={s.label}>County</label>
              <input style={s.input} placeholder="e.g. Nairobi"
                value={form.county} onChange={set('county')} />
            </div>
            <div style={s.half}>
              <label style={s.label}>Town</label>
              <input style={s.input} placeholder="e.g. Westlands"
                value={form.town} onChange={set('town')} />
            </div>
          </div>
        </div>

        {/* Section: Owner Details */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Your Details</h2>

          <label style={s.label}>Your Name *</label>
          <input style={s.input} placeholder="e.g. Jane Kamau"
            value={form.owner_name} onChange={set('owner_name')} />

          <label style={s.label}>Phone Number * <span style={s.hint}>(used to sign in + receive WhatsApp alerts)</span></label>
          <input style={s.input} type="tel" placeholder="+254 7XX XXX XXX"
            value={form.owner_phone} onChange={set('owner_phone')} />

          <label style={s.label}>M-Pesa Number <span style={s.hint}>(optional — can set later)</span></label>
          <input style={s.input} type="tel" placeholder="+254 7XX XXX XXX"
            value={form.mpesa_phone} onChange={set('mpesa_phone')} />

          <label style={s.label}>Email <span style={s.hint}>(optional — for reports)</span></label>
          <input style={s.input} type="email" placeholder="jane@example.com"
            value={form.email} onChange={set('email')} />
        </div>

        {/* Section: Plan */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Choose Your Plan</h2>
          <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>
            14-day free trial on all plans. No card required.
          </p>
          <div style={s.planGrid}>
            {PLANS.map(plan => (
              <div
                key={plan.value}
                style={{
                  ...s.planCard,
                  border: form.plan === plan.value
                    ? `2px solid ${C.orange}`
                    : '2px solid #333',
                  position: 'relative',
                }}
                onClick={() => setForm(f => ({ ...f, plan: plan.value }))}
              >
                {plan.popular && (
                  <div style={s.popularBadge}>POPULAR</div>
                )}
                <div style={s.planLabel}>{plan.label}</div>
                <div style={s.planPrice}>{plan.price}</div>
                <ul style={s.planFeatures}>
                  {plan.features.map((f, i) => (
                    <li key={i} style={s.planFeature}>✓ {f}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Section: PIN */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Set Your PIN</h2>
          <p style={{ color: C.muted, fontSize: 13, marginBottom: 20 }}>
            This is how you'll sign in. Share it only with trusted staff.
          </p>

          <PinPad
            label="Your PIN *"
            value={form.pin}
            onChange={v => setForm(f => ({ ...f, pin: v }))}
          />

          <PinPad
            label="Confirm PIN *"
            value={form.confirm_pin}
            onChange={v => setForm(f => ({ ...f, confirm_pin: v }))}
          />
        </div>

        {/* Error */}
        {error && (
          <div style={s.errorBox}>{error}</div>
        )}

        {/* Submit */}
        <button
          style={{ ...s.submitBtn, opacity: loading ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Setting up your venue…' : 'CREATE MY VENUE'}
        </button>

        <p style={s.terms}>
          By signing up you agree to our Terms of Service and Privacy Policy.
        </p>

      </div>
    </div>
  );
}

const s = {
  page:        { minHeight: '100vh', backgroundColor: C.bg, padding: '32px 16px' },
  container:   { maxWidth: 480, margin: '0 auto' },

  header:      { textAlign: 'center', marginBottom: 32 },
  logo:        { color: C.orange, fontSize: 42, fontWeight: 700, letterSpacing: 2, marginBottom: 4 },
  subtitle:    { color: C.muted, fontSize: 15 },
  refBadge:    { display: 'inline-block', marginTop: 12, background: '#2a2a2a',
                 border: '1px solid #444', borderRadius: 6, padding: '6px 12px',
                 color: C.muted, fontSize: 12 },

  section:     { background: C.surface, borderRadius: 12, padding: 24, marginBottom: 16 },
  sectionTitle:{ color: C.orange, fontSize: 16, fontWeight: 700, letterSpacing: 1,
                 marginBottom: 16, textTransform: 'uppercase' },

  label:       { display: 'block', color: C.muted, fontSize: 12, fontWeight: 600,
                 letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, marginTop: 12 },
  hint:        { color: '#666', fontWeight: 400, textTransform: 'none', letterSpacing: 0 },

  input:       { width: '100%', padding: '12px 14px', background: C.input,
                 border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                 fontSize: 15, marginBottom: 4, boxSizing: 'border-box',
                 fontFamily: "'Barlow Condensed', sans-serif" },
  select:      { width: '100%', padding: '12px 14px', background: C.input,
                 border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                 fontSize: 15, marginBottom: 4, boxSizing: 'border-box',
                 fontFamily: "'Barlow Condensed', sans-serif" },

  charCount:   { position: 'absolute', right: 12, top: 12, color: '#666', fontSize: 12 },

  row:         { display: 'flex', gap: 12 },
  half:        { flex: 1 },

  // Plan cards
  planGrid:    { display: 'flex', flexDirection: 'column', gap: 12 },
  planCard:    { background: C.input, borderRadius: 10, padding: '16px 20px', cursor: 'pointer' },
  popularBadge:{ position: 'absolute', top: -10, right: 12, background: C.orange,
                 color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px',
                 borderRadius: 4, letterSpacing: 1 },
  planLabel:   { color: C.text, fontSize: 20, fontWeight: 700, marginBottom: 2 },
  planPrice:   { color: C.orange, fontSize: 15, fontWeight: 600, marginBottom: 10 },
  planFeatures:{ listStyle: 'none', padding: 0, margin: 0 },
  planFeature: { color: C.muted, fontSize: 13, marginBottom: 4 },

  // PIN pad
  pinDots:     { display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16, marginTop: 8 },
  dot:         { width: 18, height: 18, borderRadius: '50%', border: `2px solid ${C.orange}`,
                 transition: 'background 0.15s' },
  numpad:      { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 },
  numKey:      { padding: '14px 0', background: C.input, border: 'none', borderRadius: 8,
                 color: C.text, fontSize: 20, fontWeight: 600, cursor: 'pointer',
                 fontFamily: "'Barlow Condensed', sans-serif" },

  errorBox:    { background: '#2a1a1a', border: `1px solid ${C.danger}`, borderRadius: 8,
                 padding: '14px 16px', color: '#e87c7c', fontSize: 14, marginBottom: 16 },

  submitBtn:   { width: '100%', padding: '16px 0', background: C.orange, border: 'none',
                 borderRadius: 10, color: '#fff', fontSize: 20, fontWeight: 700,
                 cursor: 'pointer', letterSpacing: 1, marginBottom: 16,
                 fontFamily: "'Barlow Condensed', sans-serif" },
  terms:       { color: '#555', fontSize: 12, textAlign: 'center' },
};

